import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { formatDateTime } from "../../../shared/time";
import type { Announcement, PublishedPage } from "../../../shared/types";
import { Badge, ErrorNote } from "../../ui";
import { PageFrame, messageOf } from "../calendar/chrome";
import { formatDateKey } from "../calendar/dates";
import { Article } from "../publishing/Article";

interface PortalPayload {
  personName: string;
  assignments: Array<{ id: string; status: string; planId: string; planTitle: string; startsAt: string; position: string }>;
  groups: Array<{ id: string; name: string; meetingTime: string | null; meetingPlace: string | null }>;
  announcements: Announcement[];
  pages: PublishedPage[];
  events: Array<{ id: string; title: string; startsAt: string; endsAt: string; place: string }>;
}

function whenLabel(value: string | null): string {
  if (!value) return "Time not set";
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value);
  return value;
}

export function PortalPage() {
  const [portal, setPortal] = useState<PortalPayload | null>(null);
  const [error, setError] = useState("");
  const [openPage, setOpenPage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api<PortalPayload>("/api/v1/portal")
      .then((payload) => { if (live) setPortal(payload); })
      .catch((caught: unknown) => { if (live) setError(messageOf(caught)); });
    return () => { live = false; };
  }, []);

  const next = portal?.assignments[0];
  const later = portal?.assignments.slice(1) ?? [];
  const open = portal?.pages.find((page) => page.id === openPage) ?? null;

  return (
    <PageFrame kicker="Portal" title={portal?.personName ?? "Member portal"} lede="Published pages, your groups, and the plans attached to this login. Messages and payments are not sent from here.">
      {error && <ErrorNote>{error}</ErrorNote>}
      {!portal && !error ? <p>Loading the room…</p> : null}
      {portal && (
        <div className="stack">
          <article className="panel panel-pad stack">
            <p className="kicker">Next up</p>
            {next ? (
              <>
                <h2 className="page-title" style={{ fontSize: 32 }}>{next.position}</h2>
                <p>{next.planTitle}</p>
                <p className="muted">{formatDateTime(next.startsAt)}</p>
                <div className="cluster">
                  <Badge tone={next.status === "accepted" ? "teal" : next.status === "declined" ? "danger" : "amber"}>{next.status}</Badge>
                  <Link className="btn" to={`/services/${next.planId}`}>Open the plan</Link>
                </div>
              </>
            ) : <p className="muted">No upcoming assignment is attached to this login.</p>}
          </article>
          <div className="grid-2">
            <article className="panel panel-pad stack">
              <h2>Other assignments</h2>
              {later.length === 0 ? <p className="muted">Nothing else on the list.</p> : later.map((item) => (
                <Link key={item.id} className="list-row" to={`/services/${item.planId}`}>
                  <span className="time">{formatDateTime(item.startsAt)}</span>
                  <span><strong>{item.position}</strong><div className="muted">{item.planTitle}</div></span>
                  <Badge tone={item.status === "accepted" ? "teal" : item.status === "declined" ? "danger" : "amber"}>{item.status}</Badge>
                </Link>
              ))}
            </article>
            <article className="panel panel-pad stack">
              <h2>Groups</h2>
              {portal.groups.length === 0 ? <p className="muted">No group membership on this login.</p> : portal.groups.map((group) => (
                <Link key={group.id} className="list-row" to={`/groups/${group.id}`} style={{ gridTemplateColumns: "1fr" }}>
                  <span><strong>{group.name}</strong><div className="muted">{whenLabel(group.meetingTime)}{group.meetingPlace ? ` · ${group.meetingPlace}` : ""}</div></span>
                </Link>
              ))}
            </article>
          </div>
          <div className="grid-2">
            <article className="panel panel-pad stack">
              <h2>Announcements</h2>
              {portal.announcements.length === 0 ? <p className="muted">Nothing published.</p> : portal.announcements.map((note) => (
                <div key={note.id}>
                  <strong>{note.title}</strong>
                  {note.publishOn ? <div className="faint">{formatDateKey(note.publishOn)}</div> : null}
                  <p className="prose muted">{note.body}</p>
                </div>
              ))}
            </article>
            <article className="panel panel-pad stack">
              <h2>Coming events</h2>
              {portal.events.length === 0 ? <p className="muted">No upcoming events.</p> : portal.events.map((event) => (
                <Link key={event.id} className="list-row" to={`/calendar/${event.id}`}>
                  <span className="time">{formatDateTime(event.startsAt)}</span>
                  <span><strong>{event.title}</strong><div className="muted">{event.place || "Place not set"}</div></span>
                  <span />
                </Link>
              ))}
            </article>
          </div>
          <article className="panel panel-pad stack">
            <h2>Published pages</h2>
            {portal.pages.length === 0 ? <p className="muted">No pages are published.</p> : portal.pages.map((page) => (
              <button key={page.id} className="btn choice" type="button" aria-pressed={openPage === page.id} onClick={() => setOpenPage((current) => current === page.id ? null : page.id)}>
                <span>{page.title}</span>
                <span>{openPage === page.id ? "Close" : "Read"}</span>
              </button>
            ))}
            {open && <Article kicker="Published" title={open.title} body={open.body} />}
          </article>
        </div>
      )}
    </PageFrame>
  );
}
